import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOHead from "@/components/feature/SEOHead";
import SchemaOrg from "@/components/feature/SchemaOrg";
import ComicCard from "@/components/feature/ComicCard";
import { comics, categories } from "@/mocks/comics";
import NotFound from "@/pages/NotFound";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const sortOptions = [
    { value: "latest", label: "最新更新" },
    { value: "popular", label: "最受欢迎" },
    { value: "rating", label: "评分最高" },
  ];

  const statusOptions = [
    { value: "all", label: "全部状态" },
    { value: "连载中", label: "连载中" },
    { value: "已完结", label: "已完结" },
  ];

  const [sortBy, setSortBy] = useState("latest");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    setSortBy("latest");
    setStatusFilter("all");
  }, [slug]);

  if (!category) {
    return <NotFound />;
  }

  const categoryComics = comics.filter((c) => {
    if (category.slug === "all") return true;
    return c.category === category.slug;
  });

  const filteredComics = categoryComics
    .filter((c) => (statusFilter === "all" ? true : c.status === statusFilter))
    .sort((a, b) => {
      if (sortBy === "popular") return b.views - a.views;
      if (sortBy === "rating") return b.rating - a.rating;
      return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    });

  const catName = category.slug === "all" ? "全部漫画" : category.name + "漫画";

  return (
    <>
      <SEOHead
        title={`虚空韩漫网 - ${catName} | 漫画大全_韩漫完整在线观看`}
        description={`虚空韩漫网提供大量${catName}免费在线观看，收录最新最全的${catName}资源，每日更新、画质清晰，是韩漫爱好者的阅读天堂。`}
        keywords={`虚空韩漫网,${catName},韩漫画免费网站,韩漫在线观看,韩国漫画免费`}
        canonicalUrl={`https://www.xukonghanman.com/category/${slug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${catName} - 虚空韩漫网`,
          url: `https://www.xukonghanman.com/category/${slug}`,
          description: `虚空韩漫网${catName}分类页面`,
          isPartOf: {
            "@type": "WebSite",
            name: "虚空韩漫网",
            url: "https://www.xukonghanman.com/",
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.xukonghanman.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: catName,
            },
          ],
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                {catName}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共找到 {filteredComics.length} 部漫画
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 p-3 bg-background-100 rounded-xl">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              <div className="flex items-center gap-1 px-1 py-1 bg-background-200 rounded-full">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="w-px h-5 bg-background-300 hidden sm:block"></div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-500 whitespace-nowrap">状态：</span>
              <div className="flex items-center gap-1 px-1 py-1 bg-background-200 rounded-full">
                {statusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setStatusFilter(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      statusFilter === opt.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <span className="w-10 h-10 flex items-center justify-center text-foreground-300">
                  <i className="ri-inbox-line text-3xl"></i>
                </span>
              </div>
              <p className="text-foreground-500 text-sm">没有找到符合条件的漫画</p>
              <button
                onClick={() => {
                  setSortBy("latest");
                  setStatusFilter("all");
                }}
                className="mt-4 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          )}

          <div className="mt-10">
            <h2 className="text-base font-semibold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              其他分类
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === "all" ? "/" : `/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={cat.icon}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}